import React, { useState, useEffect } from 'react';
import './i18n';
import { Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import BuildingsPage from './pages/BuildingsPage';
import RequestsPage from './pages/RequestsPage';
import StatsPage from './pages/StatsPage';
import SettingsPage from './pages/SettingsPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import plPL from 'antd/locale/pl_PL';
import enUS from 'antd/locale/en_US';
import api from './services/api';
import i18n from './i18n';

const SettingsLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data } = useQuery({
    queryKey: ['public-settings'],
    queryFn: async () => {
      const response = await api.get('/public-settings');
      return response.data;
    },
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (data?.general?.systemName) {
      document.title = data.general.systemName;
    }
  }, [data]);

  return <>{children}</>;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const [antdLocale, setAntdLocale] = useState(i18n.language === 'pl' ? plPL : enUS);

  // Subscribe to i18n language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setAntdLocale(i18n.language === 'pl' ? plPL : enUS);
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  return (
    <AuthProvider>
      <SettingsLoader>
        <ConfigProvider locale={antdLocale}>
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="buildings" element={<BuildingsPage />} />
            <Route path="requests" element={<RequestsPage />} />
            <Route path="stats" element={<StatsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
        </Routes>
        </ConfigProvider>
      </SettingsLoader>
    </AuthProvider>
  );
};

export default App;
